// contexts/NotificationContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import { socket, registerUser } from '../utils/socket';
import { apiWithPrefix as api } from '../utils/api';
import { useAuth } from './AuthContext';
import toast from '../utils/toast';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Fetch notifications from API
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await api.get('/notifications/my');
      
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        setNotifications(response.data.data);
        setUnreadCount(response.data.data.filter(n => !n.isRead).length);
      } else if (Array.isArray(response.data)) {
        // Fallback for old format
        setNotifications(response.data);
        setUnreadCount(response.data.filter(n => !n.isRead).length);
      } else {
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Failed to fetch HRMS notifications:', error);
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Register user for Socket.IO notifications when user is available
  useEffect(() => {
    if (user?.email) {
      registerUser(user.email);
      // Store email for socket reconnection
      localStorage.setItem('userEmail', user.email);
    }
  }, [user]);

  // Listen for real-time notification events
  useEffect(() => {
    const handleNewNotification = (newNotification) => {
      console.log('📩 Received HRMS notification:', newNotification);
      
      setNotifications(prev => [newNotification, ...prev]);
      setUnreadCount(prev => prev + 1);
      
      // Show browser toast notification
      toast.success(newNotification.title, {
        description: newNotification.message,
        duration: 5000,
      });

      // Show browser system notification if permission granted
      if (Notification.permission === 'granted') {
        new Notification(newNotification.title, {
          body: newNotification.message,
          icon: '/favicon.ico',
          tag: newNotification.id,
        });
      }
    };

    // Listen for new notifications
    socket.on('notification:new', handleNewNotification);

    // Cleanup listener on unmount
    return () => {
      socket.off('notification:new', handleNewNotification);
    };
  }, []);

  // Initial fetch when component mounts
  useEffect(() => {
    if (user?.email) {
      fetchNotifications();
    }
  }, [user]);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      const response = await api.put(`/notifications/${notificationId}/read`);
      if (response.data && response.data.success) {
        setNotifications(prev =>
          prev.map(n =>
            n.id === notificationId
              ? { ...n, isRead: true, readAt: new Date().toISOString() }
              : n
          )
        );
        setUnreadCount(prev => Math.max(prev - 1, 0));
        return true;
      }
    } catch (error) {
      console.error('Failed to mark HRMS notification as read:', error);
      return false;
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      const response = await api.put('/notifications/my/mark-all-read');
      if (response.data && response.data.success) {
        setNotifications(prev =>
          prev.map(n => ({ ...n, isRead: true, readAt: new Date().toISOString() }))
        );
        setUnreadCount(0);
        return true;
      }
    } catch (error) {
      console.error('Failed to mark all HRMS notifications as read:', error);
      return false;
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId) => {
    try {
      const response = await api.delete(`/notifications/${notificationId}`);
      if (response.data && response.data.success) {
        setNotifications(prev => {
          const updated = prev.filter(n => n.id !== notificationId);
          const unread = updated.filter(n => !n.isRead).length;
          setUnreadCount(unread);
          return updated;
        });
        return true;
      }
    } catch (error) {
      console.error('Failed to delete HRMS notification:', error);
      return false;
    }
  };

  // Get unread count
  const getUnreadCount = async () => {
    try {
      const response = await api.get('/notifications/my/unread-count');
      if (response.data && response.data.success) {
        setUnreadCount(response.data.data.count);
        return response.data.data.count;
      }
    } catch (error) {
      console.error('Failed to get HRMS unread count:', error);
      return 0;
    }
  };

  // Request browser notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      console.log('HRMS Notification permission:', permission);
      return permission;
    }
    return Notification.permission;
  };

  const value = {
    notifications,
    setNotifications,
    unreadCount,
    setUnreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    getUnreadCount,
    requestNotificationPermission,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;